import React, { useState, useEffect } from "react";
import apiService from "../../shared/apiService";
import { useTranslation } from "../../contexts/LanguageContext";
import {
  X,
  Target,
  DollarSign,
  Users,
  TrendingUp,
  ChevronRight,
  Info,
  CheckCircle2,
  Search,
  MapPin,
  Zap,
  Plus,
} from "lucide-react";

interface BoostMetaModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: any) => Promise<void>;
  loading: boolean;
  post: any;
}

const BoostMetaModal: React.FC<BoostMetaModalProps> = ({
  open,
  onClose,
  onConfirm,
  loading,
}) => {
  const { t } = useTranslation();
  const [dailyBudget, setDailyBudget] = useState(20);
  const [duration, setDuration] = useState(5);
  const [ageRange, setAgeRange] = useState([18, 65]);
  const [gender, setGender] = useState<"all" | "male" | "female">("all");
  const [objective, setObjective] = useState("ENGAGEMENT");
  const [location, setLocation] = useState("Brasil");
  const [interestSearch, setInterestSearch] = useState("");
  const [selectedInterests, setSelectedInterests] = useState<any[]>([]);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (interestSearch.length >= 3) {
        setSearching(true);
        try {
          const data = await apiService.get(
            "/ads/meta/search-detailed-targeting",
            {
              q: interestSearch,
              type: "adinterest",
            },
          );
          if (data.success) {
            setSearchResults(data.data || []);
          }
        } catch (err) {
          console.error("Erro na busca de interesses:", err);
        } finally {
          setSearching(false);
        }
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [interestSearch]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const totalBudget = dailyBudget * duration;
  const endDate = new Date();
  endDate.setDate(endDate.getDate() + duration);

  const handleSubmit = async () => {
    await onConfirm({
      daily_budget_brl: dailyBudget,
      duration_days: duration,
      age_min: ageRange[0],
      age_max: ageRange[1],
      gender,
      objective,
      location,
      interests: selectedInterests,
    });
  };

  const addInterest = (interest: any) => {
    if (!selectedInterests.find((i) => i.id === interest.id)) {
      setSelectedInterests([...selectedInterests, interest]);
    }
    setInterestSearch("");
    setSearchResults([]);
  };

  const removeInterest = (id: string) => {
    setSelectedInterests(selectedInterests.filter((i) => i.id !== id));
  };

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center bg-black/60 backdrop-blur-xs animate-in fade-in duration-300">
      <div className="bg-[#121214] border border-white/10 w-full max-w-4xl rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-300 max-h-[90vh]">
        {/* Header */}
        <div className="px-8 py-6 border-b border-white/5 flex items-center justify-between bg-linear-to-r from-(--color-primary-600)/20 to-purple-600/10">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-(--color-primary-600) rounded-2xl shadow-xl shadow-(--color-primary-600)/20">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-black text-white tracking-tight uppercase">
                {t("Turbinar Publicação")}
              </h2>
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-1">
                {t("Configuração Avançada de Meta Ads")}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors text-gray-400 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content - Two Columns Layout */}
        <div className="p-8 space-y-8 overflow-y-auto max-h-[65vh] custom-scrollbar bg-[#121214]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Left Column: Objective and Budget */}
            <div className="space-y-10">
              {/* Objective Selection */}
              <section className="space-y-4">
                <h3 className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] flex items-center gap-2">
                  <Target className="w-4 h-4 text-(--color-primary-500)" />
                  {t("Objetivo Principal")}
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  <button
                    onClick={() => setObjective("ENGAGEMENT")}
                    className={`p-5 rounded-[24px] border-2 text-left transition-all group ${
                      objective === "ENGAGEMENT"
                        ? "border-(--color-primary-500) bg-(--color-primary-500)/10"
                        : "border-white/5 bg-white/5 hover:border-white/20"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div
                        className={`p-3 rounded-xl ${objective === "ENGAGEMENT" ? "bg-(--color-primary-500) text-white shadow-lg shadow-(--color-primary-500)/30" : "bg-white/10 text-gray-400 group-hover:text-white"}`}
                      >
                        <Target className="w-6 h-6" />
                      </div>
                      {objective === "ENGAGEMENT" && (
                        <CheckCircle2 className="w-5 h-5 text-(--color-primary-500)" />
                      )}
                    </div>
                    <div className="font-black text-white text-lg tracking-tight">
                      {t("Engajamento")}
                    </div>
                    <div className="text-[11px] text-gray-500 font-medium mt-1 leading-relaxed">
                      {t(
                        "Focado em curtidas, comentários e interações no seu post.",
                      )}
                    </div>
                  </button>

                  <button
                    onClick={() => setObjective("OUTCOME_TRAFFIC")}
                    className={`p-5 rounded-[24px] border-2 text-left transition-all group ${
                      objective === "OUTCOME_TRAFFIC"
                        ? "border-(--color-info) bg-(--color-info)/10"
                        : "border-white/5 bg-white/5 hover:border-white/20"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div
                        className={`p-3 rounded-xl ${objective === "OUTCOME_TRAFFIC" ? "bg-(--color-info) text-white shadow-lg shadow-(--color-info)/30" : "bg-white/10 text-gray-400 group-hover:text-white"}`}
                      >
                        <TrendingUp className="w-6 h-6" />
                      </div>
                      {objective === "OUTCOME_TRAFFIC" && (
                        <CheckCircle2 className="w-5 h-5 text-(--color-info)" />
                      )}
                    </div>
                    <div className="font-black text-white text-lg tracking-tight">
                      {t("Tráfego")}
                    </div>
                    <div className="text-[11px] text-gray-500 font-medium mt-1 leading-relaxed">
                      {t(
                        "Ideal para levar pessoas para o seu site ou perfil externo.",
                      )}
                    </div>
                  </button>
                </div>
              </section>

              {/* Budget and Duration */}
              <section className="space-y-6 bg-white/5 p-6 rounded-[32px] border border-white/5">
                <div className="flex items-center justify-between">
                  <h3 className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-500" />
                    {t("Investimento")}
                  </h3>
                  <div className="bg-green-500/10 px-4 py-1.5 rounded-full border border-green-500/20">
                    <span className="text-xs font-black text-green-500">
                      {t("Total: R$ {total}", {
                        total: totalBudget.toFixed(2),
                      })}
                    </span>
                  </div>
                </div>

                <div className="space-y-8">
                  <div className="space-y-4">
                    <div className="flex justify-between text-[11px] font-black uppercase tracking-widest">
                      <span className="text-gray-500">{t("Diário")}</span>
                      <span className="text-primary-400">R$ {dailyBudget}</span>
                    </div>
                    <input
                      type="range"
                      min="6"
                      max="500"
                      value={dailyBudget}
                      onChange={(e) => setDailyBudget(parseInt(e.target.value))}
                      className="w-full accent-(--color-primary-500) h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between text-[11px] font-black uppercase tracking-widest">
                      <span className="text-gray-500">{t("Duração")}</span>
                      <span className="text-primary-400">
                        {t("{days} dias", { days: duration })}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="30"
                      value={duration}
                      onChange={(e) => setDuration(parseInt(e.target.value))}
                      className="w-full accent-(--color-primary-500) h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                </div>
              </section>
            </div>

            {/* Right Column: Audience */}
            <div className="space-y-10">
              <section className="space-y-6">
                <h3 className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-400" />
                  {t("Público Alvo")}
                </h3>

                <div className="bg-white/5 p-8 rounded-[32px] border border-white/5 space-y-8">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <label className="text-[10px] text-gray-500 uppercase font-black tracking-widest ml-1">
                        {t("Gênero")}
                      </label>
                      <div className="flex bg-black/40 p-1 rounded-2xl border border-white/5 gap-1">
                        {["all", "male", "female"].map((g) => (
                          <button
                            key={g}
                            onClick={() => setGender(g as any)}
                            className={`flex-1 py-2 px-1 text-[10px] font-black uppercase rounded-xl transition-all whitespace-nowrap overflow-hidden text-ellipsis ${gender === g ? "bg-(--color-primary-600) text-white shadow-lg" : "text-gray-500 hover:text-gray-300"}`}
                          >
                            {g === "all"
                              ? t("Todos")
                              : g === "male"
                                ? t("Homens")
                                : t("Mulheres")}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <label className="text-[10px] text-gray-500 uppercase font-black tracking-widest ml-1">
                        {t("Faixa Etária")}
                      </label>
                      <div className="flex items-center gap-3 bg-black/40 p-1.5 rounded-2xl border border-white/5">
                        <input
                          type="number"
                          min="13"
                          max="65"
                          value={ageRange[0]}
                          onChange={(e) =>
                            setAgeRange([parseInt(e.target.value), ageRange[1]])
                          }
                          className="w-full bg-transparent border-0 text-xs text-white text-center focus:ring-0"
                        />
                        <span className="text-gray-600 font-bold">~</span>
                        <input
                          type="number"
                          min="13"
                          max="65"
                          value={ageRange[1]}
                          onChange={(e) =>
                            setAgeRange([ageRange[0], parseInt(e.target.value)])
                          }
                          className="w-full bg-transparent border-0 text-xs text-white text-center focus:ring-0"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 relative">
                    <label className="text-[10px] text-gray-500 uppercase font-black tracking-widest ml-1 flex items-center gap-2">
                      <Search className="w-3 h-3" />
                      {t("Segmentação Detalhada")}
                    </label>

                    <div className="relative group">
                      <Plus className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-(--color-primary-500)" />
                      <input
                        type="text"
                        value={interestSearch}
                        onChange={(e) => setInterestSearch(e.target.value)}
                        placeholder={t("Pesquisar interesses na Meta...")}
                        className="w-full h-[54px] pl-12 pr-12 bg-black/40 border border-white/5 rounded-2xl text-xs font-bold text-white placeholder:text-gray-600 focus:border-(--color-primary-500)/50 outline-hidden transition-all focus:ring-8 focus:ring-(--color-primary-500)/5"
                      />
                      {searching && (
                        <div className="absolute right-4 top-1/2 -translate-y-1/2">
                          <div className="w-4 h-4 border-2 border-(--color-primary-500) border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}

                      {/* Search Results Dropdown - Professional Style */}
                      {searchResults.length > 0 && (
                        <div className="absolute z-50 left-0 right-0 top-full mt-2 bg-[#1e1e22] border border-white/10 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] max-h-60 overflow-y-auto custom-scrollbar animate-in slide-in-from-top-2 duration-200">
                          {searchResults.map((result) => (
                            <button
                              key={result.id}
                              onClick={() => addInterest(result)}
                              className="w-full text-left px-6 py-4 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0 flex items-center justify-between group/item"
                            >
                              <div>
                                <div className="text-[11px] text-white font-black uppercase tracking-tight">
                                  {result.name}
                                </div>
                                <div className="text-[9px] text-gray-500 font-bold uppercase tracking-widest mt-0.5">
                                  {result.type?.replace(
                                    "adinterest",
                                    t("Interesse"),
                                  )}
                                </div>
                              </div>
                              <Plus className="w-4 h-4 text-gray-600 group-hover/item:text-(--color-primary-500) transition-colors" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Selected Interests Tags */}
                    <div className="flex flex-wrap gap-2 pt-2 min-h-[40px]">
                      {selectedInterests.map((interest) => (
                        <div
                          key={interest.id}
                          className="flex items-center gap-2 bg-(--color-primary-500)/10 border border-(--color-primary-500)/20 text-primary-400 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-tight animate-in zoom-in-95"
                        >
                          {interest.name}
                          <button
                            onClick={() => removeInterest(interest.id)}
                            className="hover:text-red-400 transition-colors"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                      {selectedInterests.length === 0 && !interestSearch && (
                        <div className="text-[10px] text-gray-600 italic font-bold ml-2 mt-2">
                          {t(
                            "Busque interesses acima para segmentar seu público.",
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="text-[10px] text-gray-500 uppercase font-black tracking-widest ml-1">
                      {t("Localização")}
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <input
                        type="text"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder={t("Ex: Brasil, São Paulo...")}
                        className="w-full h-[50px] pl-12 pr-6 bg-black/40 border border-white/5 rounded-2xl text-xs font-bold text-white placeholder:text-gray-600 focus:border-(--color-primary-500)/50 outline-hidden transition-all"
                      />
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>

          {/* Info Banner */}
          <div className="p-6 bg-blue-600/5 border border-blue-600/10 rounded-[32px] flex gap-4">
            <div className="p-3 bg-blue-600/10 rounded-2xl h-fit">
              <Info className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h4 className="text-xs font-black text-blue-400 uppercase tracking-widest mb-1">
                {t("Nota de Veiculação")}
              </h4>
              <p className="text-[11px] text-blue-200/60 leading-relaxed font-medium">
                {t(
                  "Ao turbinar esta publicação, ela será enviada para aprovação na Meta. Sua conta de anúncios será cobrada conforme o orçamento definido. As UTMs de rastreamento profissional serão inseridas automaticamente em todos os links.",
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/5 bg-black/40 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-sm font-medium text-gray-400 hover:text-white transition-colors"
          >
            {t("Cancelar")}
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="relative group overflow-hidden px-10 py-3 rounded-2xl bg-(--color-primary-600) text-white font-black text-xs uppercase tracking-widest shadow-2xl shadow-(--color-primary-600)/30 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:pointer-events-none"
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>{t("Processando...")}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span>{t("Confirmar Turbinamento")}</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BoostMetaModal;
